'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { idbStorage } from '@/lib/idb-storage';

interface ReaderPrefs {
  fontSize: number;
  lineHeight: number;
  fontFamily: string;
  theme: 'light' | 'sepia' | 'dark';
  maxWidth: number;
}

interface ReaderState extends ReaderPrefs {
  bookBlob: Blob | null;
  fileName: string | null;
  bookTitle: string | null;
  bookAuthors: string[];
  downloadUrl: string | null;
  libraryId: string | null;
  currentChapterId: string | null;
  scrollPosition: number;

  openBook: (blob: Blob, fileName: string, title?: string, authors?: string[], downloadUrl?: string, libraryId?: string) => void;
  closeBook: () => void;
  setChapter: (id: string) => void;
  setScrollPosition: (pos: number) => void;
  setFontSize: (size: number) => void;
  setLineHeight: (height: number) => void;
  setFontFamily: (family: string) => void;
  setTheme: (theme: 'light' | 'sepia' | 'dark') => void;
  setMaxWidth: (width: number) => void;
}

export const useReaderStore = create<ReaderState>()(
  persist(
    (set) => ({
      bookBlob: null,
      fileName: null,
      bookTitle: null,
      bookAuthors: [],
      downloadUrl: null,
      libraryId: null,
      currentChapterId: null,
      scrollPosition: 0,
      fontSize: 18,
      lineHeight: 1.6,
      fontFamily: 'serif',
      theme: 'light',
      maxWidth: 720,

      openBook: (blob, fileName, title, authors, downloadUrl, libraryId) =>
        set({
          bookBlob: blob,
          fileName,
          bookTitle: title || fileName,
          bookAuthors: authors || [],
          downloadUrl: downloadUrl || null,
          libraryId: libraryId || null,
          currentChapterId: null,
          scrollPosition: 0,
        }),
      closeBook: () =>
        set({
          bookBlob: null,
          fileName: null,
          bookTitle: null,
          bookAuthors: [],
          downloadUrl: null,
          libraryId: null,
          currentChapterId: null,
          scrollPosition: 0,
        }),
      setChapter: (id) => set({ currentChapterId: id, scrollPosition: 0 }),
      setScrollPosition: (pos) => set({ scrollPosition: pos }),
      setFontSize: (size) => set({ fontSize: size }),
      setLineHeight: (height) => set({ lineHeight: height }),
      setFontFamily: (family) => set({ fontFamily: family }),
      setTheme: (theme) => set({ theme }),
      setMaxWidth: (width) => set({ maxWidth: width }),
    }),
    {
      name: 'bookshelf-reader-prefs',
      storage: createJSONStorage(() => idbStorage),
      // Only persist preferences, not the blob or reading position
      partialize: (state) => ({
        fontSize: state.fontSize,
        lineHeight: state.lineHeight,
        fontFamily: state.fontFamily,
        theme: state.theme,
        maxWidth: state.maxWidth,
      }),
    }
  )
);
